import type { Theme } from '@mui/material/styles';
import type { SxStyles } from '@/ui/style/types';

/**
 * Gets custom sx classes for the navigation bar.
 *
 * @param theme - The theme object
 * @returns The sx classes object
 */
export const getSxClasses = (theme: Theme): SxStyles => ({
  navBarRef: {
    width: 'min-content !important',
    position: 'absolute',
    right: theme.spacing(7),
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    marginRight: 0,
    zIndex: 150,
    pointerEvents: 'all',
    backgroundColor: 'transparent',
    transition: 'top 300ms ease-in-out, bottom 300ms ease-in-out',
    top: '180px',
    bottom: '55px',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexWrap: 'wrap-reverse',
    gap: '15px',
  },
  navBtnGroupContainer: {
    display: 'flex',
    position: 'relative',
    pointerEvents: 'auto',
    overflowY: 'hidden',
    padding: 5,
    flexDirection: 'column',
  },
  navBtnGroup: {
    borderRadius: theme.spacing(5),
    backgroundColor: theme.palette.geoViewColor.bgColor.light[500],
    overflow: 'clip',
    '& .MuiButtonGroup-grouped:not(:last-child)': {
      borderColor: theme.palette.geoViewColor.bgColor.light[900],
    },
  },
  navBtnGroupColumns: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
  },
  navButton: {
    backgroundColor: theme.palette.geoViewColor.bgColor.light[500],
    color: theme.palette.geoViewColor.bgColor.dark[900],
    borderRadius: 0,
    width: '44px',
    height: '44px',
    maxWidth: '44px',
    minWidth: '44px',
    padding: 'initial',
    transition: 'background-color 0.3s ease-in-out',
    '&:not(:last-of-type)': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottom: `1px solid ${theme.palette.geoViewColor.bgColor.light[900]}`,
    },
    '&:not(:first-of-type)': {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.geoViewColor.bgColor.light[500],
      color: theme.palette.geoViewColor.bgColor.dark[700],
    },
    '&:focus': {
      backgroundColor: theme.palette.geoViewColor.bgColor.light[500],
      color: theme.palette.geoViewColor.bgColor.dark[700],
    },
    '&:active': {
      backgroundColor: theme.palette.geoViewColor.bgColor.light[500],
      color: theme.palette.geoViewColor.bgColor.dark[950],
    },
  },
  popoverTitle: {
    fontSize: theme.palette.geoViewFontSize?.default ?? theme.typography.fontSize,
    fontWeight: '700',
    color: theme.palette.geoViewColor?.textColor.main ?? theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.geoViewColor?.bgColor.dark[100] ?? theme.palette.divider}`,
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(11),
    paddingLeft: theme.spacing(11),
  },
  popoverContent: {
    '&.MuiDialogContent-root': {
      paddingTop: theme.spacing(11),
      paddingBottom: theme.spacing(11),
      paddingLeft: theme.spacing(11),
    },
  },
  button: {
    justifyContent: 'flex-start',
    '&[aria-pressed="true"]': {
      backgroundColor: theme.palette.geoViewColor?.primary.main ?? theme.palette.primary.main,
      color: theme.palette.geoViewColor?.white ?? theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.geoViewColor?.primary.dark[200] ?? theme.palette.primary.dark,
      },
    },
  },
});
